/**
 * Gemensamt för kundformulären: "Ny kund" (NyKundDialog) på Affärer –
 * Pipeline och Kunder-listan. Org.nr-kontroll, tolkning av bokningsmail
 * från Comdeva, punktlistor i textfält och kundformulärets fält.
 */

export const COMPANY_INDUSTRIES = [
  'Fastighet', 'Bygg & Entreprenad', 'Infrastruktur', 'Industri',
  'Offentlig sektor', 'Energi', 'IT & Tech', 'Teknikkonsult', 'Övrigt'
];
export const COMPANY_STATUSES_LIST = [
  { key: 'prospect', label: 'Prospect' },
  { key: 'aktiv',    label: 'Aktiv kund' },
  { key: 'vilande',  label: 'Vilande' },
];
export const EMPTY_COMPANY_FORM = {
  name: '', orgNumber: '', industry: '', website: '',
  address: '', zip: '', city: '', phone: '', notes: '',
  visitStreet: '', visitZip: '', visitCity: '', country: '',
  departmentIds: [], status: 'prospect', revenue: '', employees: '',
  linkedinUrl: '', description: '', foundedYear: '', companyType: '', specialties: '', logoUrl: '',
  segment: '', organisationstyp: '', agarform: '', kundtyp: '',
};

export const AGARFORM_OPTIONS = [
  'Statlig myndighet', 'Statligt bolag', 'Kommunalt', 'Privat',
  'Privat börsnoterat', 'Kommunalt börsnoterat', 'Kooperativt',
];

export const KUNDTYP_OPTIONS = [
  'Byggherre', 'Fastighetsägare', 'Förvaltning', 'Entreprenör',
  'Konsult', 'Myndighet', 'Övrigt',
];

// Kundens kategorier (Comdevas kategorisering). Listorna gäller i alla
// formulär, filter och exporter. Äldre fritextsegment som inte finns i
// listan visas som "Tidigare: <värde>" tills de ändras.
export const SEGMENT_OPTIONS = [
  'Vatten och avlopp', 'Energi och kraft', 'Statliga fastigheter', 'Statliga myndigheter',
  'Försvar, säkerhet och teknik', 'Hamnar och transport', 'Kommuner och regioner', 'Stora byggbolag',
  'Fastigheter', 'Anläggning', 'Tekniska konsulter', 'Mindre entreprenörer',
];

// Kort beskrivning per segment: hjälptext i segmentvalet och underlag till
// AI-förslagen i "Kategorisera kunder" (kundKategoriPrompt.js).
export const SEGMENT_BESKRIVNINGAR = {
  'Vatten och avlopp': 'VA-huvudmän, VA-bolag och kommunalförbund för vatten',
  'Energi och kraft': 'produktion, nät och distribution av el och värme',
  'Statliga fastigheter': 'statliga fastighetsägare och -förvaltare',
  'Statliga myndigheter': 'statliga myndigheter utanför övriga segment',
  'Försvar, säkerhet och teknik': 'försvar, säkerhetsklassad verksamhet och försvarsindustri',
  'Hamnar och transport': 'hamnar, flygplatser och kollektivtrafik',
  'Kommuner och regioner': 'kommuner, regioner och deras förvaltningar och fastighetsbolag',
  'Stora byggbolag': 'de stora nationella byggföretagen',
  'Fastigheter': 'fastighetsägare och förvaltare utanför staten, privata och kommunala',
  'Anläggning': 'entreprenörer med inriktning på anläggning och infrastruktur (väg, järnväg, mark, VA-ledningar)',
  'Tekniska konsulter': 'arkitekt-, teknik- och projekteringskonsulter',
  'Mindre entreprenörer': 'byggentreprenörer utanför de stora byggbolagen, med inriktning på husbyggnad',
};

export const ORGANISATIONSTYP_OPTIONS = [
  'Statlig myndighet', 'Statligt bolag', 'Kommun', 'Kommunal förvaltning', 'Kommunalt bolag',
  'Kommunalförbund', 'Region', 'Regionalt bolag', 'Bolag', 'Konsultbolag',
];

// Värdet i listan, eller '' om det inte går att tolka. Tillägg inom parentes
// ("Stora byggbolag (Bluebeam-ansvarig)", "Statlig myndighet (affärsverk)")
// ignoreras, liksom skillnader i versaler och mellanslag.
function iLista(lista, value) {
  const v = String(value || '').replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return lista.find(x => x.toLowerCase() === v) || '';
}
export const tolkaSegment = value => iLista(SEGMENT_OPTIONS, value);
export const tolkaOrganisationstyp = value => iLista(ORGANISATIONSTYP_OPTIONS, value);

/**
 * Berikning från Bolagsverket (BolagsAPI-svaret från /.netlify/functions/bolagsverket):
 * bara fält som är tomma på kunden. Namnet rörs aldrig.
 * → { fält: värde } att skriva, tomt objekt om inget saknas.
 */
export function tommaFaltFranBolagsverket(kund, data) {
  const k = kund || {}, d = data || {};
  const tomt = v => v == null || v === '' || (Array.isArray(v) && !v.length);
  const kandidater = {
    description: d.description,
    address: d.address?.street, zip: d.address?.zip, city: d.address?.city,
    visitStreet: d.visitingAddress?.street, visitZip: d.visitingAddress?.zip, visitCity: d.visitingAddress?.city,
    industry: d.sniCodes?.[0]?.label,
    website: d.website, linkedin: d.linkedin, companyForm: d.companyForm,
    fSkatt: d.fSkatt, stabilityGrade: d.stabilityGrade, registeredDate: d.registeredDate,
    sniCodes: d.sniCodes?.length ? JSON.stringify(d.sniCodes) : undefined,
  };
  return Object.fromEntries(Object.entries(kandidater).filter(([f, v]) => !tomt(v) && tomt(k[f])));
}

const SEGMENT_PALETTE = [
  '#d32f2f', '#1565c0', '#2e7d32', '#e65100',
  '#6a1b9a', '#00695c', '#558b2f', '#4527a0',
];
// Fasta, tydligt skilda färger för segmenten i listan
export const SEGMENT_FARGER = {
  'Vatten och avlopp': '#1565c0',            // blå
  'Energi och kraft': '#ef6c00',             // orange
  'Statliga fastigheter': '#6d4c41',         // brun
  'Statliga myndigheter': '#455a64',         // blågrå
  'Försvar, säkerhet och teknik': '#2e7d32', // grön
  'Hamnar och transport': '#00838f',         // turkos
  'Kommuner och regioner': '#7b1fa2',        // lila
  'Stora byggbolag': '#c62828',              // röd
  'Fastigheter': '#d81b60',                  // cerise
  'Anläggning': '#f9a825',                   // gul
  'Tekniska konsulter': '#212121',           // svart
  'Mindre entreprenörer': '#4fc3f7',         // ljusblå
};
export function segmentColor(name) {
  if (!name) return '#9e9e9e';
  if (SEGMENT_FARGER[name]) return SEGMENT_FARGER[name];
  // Äldre fritextsegment: färg härledd ur namnet, som tidigare
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return SEGMENT_PALETTE[Math.abs(h) % SEGMENT_PALETTE.length];
}

/** Infogar "• " vid markören i ett textfält (ref till TextField). */
export function insertBullet(ref, value, onChange) {
  const el = ref?.current?.querySelector('textarea');
  if (!el) { onChange(value + (value.endsWith('\n') || !value ? '' : '\n') + '• '); return; }
  const start = el.selectionStart;
  const atLineStart = start === 0 || value[start - 1] === '\n';
  const prefix = atLineStart ? '' : '\n';
  const next = value.slice(0, start) + prefix + '• ' + value.slice(start);
  onChange(next);
  requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + prefix.length + 2, start + prefix.length + 2); });
}

// Plockar ut tio siffror ur ett inklistrat värde. Tar hand om mellanslag,
// tankstreck, punkter och prefix som "Org.nr:", och kortar sekelprefixet 16.
function orgNrDigits(value) {
  let d = String(value || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('16')) d = d.slice(2);
  return /^\d{10}$/.test(d) ? d : '';
}

// Svenska organisationsnummer har en Luhn-kontrollsiffra sist. BolagsAPI avvisar
// nummer som inte stämmer, så det är bättre att fånga det här och säga varför än
// att skicka iväg ett anrop som ändå nekas.
function orgNrChecksumOk(d) {
  let sum = 0;
  for (let i = 0; i < d.length; i++) {
    let n = Number(d[i]);
    if (i % 2 === 0) n *= 2;
    if (n > 9) n -= 9;
    sum += n;
  }
  return sum % 10 === 0;
}

// Vilken sista siffra numret borde haft — gör felet åtgärdbart i stället för
// bara konstaterat.
function orgNrExpectedLastDigit(d) {
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let n = Number(d[i]);
    if (i % 2 === 0) n *= 2;
    if (n > 9) n -= 9;
    sum += n;
  }
  return (10 - sum % 10) % 10;
}

// Returnerar ett giltigt 10-siffrigt org.nr, eller '' om värdet inte är ett.
// Kontrollsiffran ingår i kravet — annars släpps kundnummer som råkar ha tio
// siffror igenom snabbfyllet från bokningsmailet.
export function normalizeOrgNr(value) {
  const d = orgNrDigits(value);
  return d && orgNrChecksumOk(d) ? d : '';
}

// Returnerar ett felmeddelande, eller '' om numret ser korrekt ut.
export function orgNrProblem(raw) {
  const d = orgNrDigits(raw);
  if (!d) {
    return `"${String(raw).trim()}" är inget giltigt org.nr — ange 10 siffror, t.ex. 556000-0000`;
  }
  if (!orgNrChecksumOk(d)) {
    const fixed = d.slice(0, 9) + orgNrExpectedLastDigit(d);
    return `${d.slice(0, 6)}-${d.slice(6)} har fel kontrollsiffra. `
      + `Med samma första nio siffror skulle numret vara ${fixed.slice(0, 6)}-${fixed.slice(6)}.`;
  }
  return '';
}

export function parseBookingEmail(str) {
  if (!str?.trim()) return null;
  const lines = str.split(/\r?\n/).map(l => l.trim());
  const emailRe = /[\w.+\-]+@[\w-]+\.[\w.]+/i; // eslint-disable-line no-useless-escape
  const phoneRe = /(\+?[0-9][0-9 ()\-]{6,}[0-9])/; // eslint-disable-line no-useless-escape
  const LABELS = new Set(['Start','Slut','Företag/Person','Kundnummer','Ort',
    'Antal anställda','Bransch','Omsättning','Plats','Ämne','Beskrivning','Resurs']);
  if (!lines.some(l => l === 'Företag/Person' || l === 'Kundnummer' || l === 'Beskrivning')) return null;
  const r = { companyName:'', orgNumber:'', city:'', employees:'', industry:'', revenue:'',
    contactName:'', contactRole:'', contactPhone:'', contactEmail:'',
    meetingStart:'', meetingPlace:'', meetingSubject:'', meetingNotes:'' };
  let i = 0, beskrivIdx = -1, motesIdx = -1;
  while (i < lines.length) {
    const l = lines[i], v = lines[i + 1] || '';
    if (l === 'Start') { r.meetingStart = v.replace(/\s*On\s*$/, '').trim(); i += 2; continue; }
    if (l === 'Företag/Person') { r.companyName = v; i += 2; continue; }
    // Kundnummer från säljsystemet är inte alltid ett organisationsnummer –
    // fyll bara i org.nr-fältet när värdet faktiskt ser ut som ett sådant.
    if (l === 'Kundnummer') { r.orgNumber = normalizeOrgNr(v); i += 2; continue; }
    if (l === 'Ort') { r.city = v; i += 2; continue; }
    if (l === 'Antal anställda') { r.employees = v; i += 2; continue; }
    if (l === 'Bransch') { r.industry = v; i += 2; continue; }
    if (l === 'Omsättning') { r.revenue = v; i += 2; continue; }
    if (l === 'Plats') { r.meetingPlace = v; i += 2; continue; }
    if (l === 'Ämne') { r.meetingSubject = v; i += 2; continue; }
    if (l === 'Beskrivning') { beskrivIdx = i + 1; i++; continue; }
    if (l.startsWith('Möteskommentar')) { motesIdx = i; i++; continue; }
    i++;
  }
  if (beskrivIdx >= 0) {
    const end = motesIdx >= 0 ? motesIdx : lines.length;
    for (const line of lines.slice(beskrivIdx, end).filter(Boolean)) {
      if (LABELS.has(line)) break;
      if (!r.contactEmail) { const m = line.match(emailRe); if (m) { r.contactEmail = m[0].toLowerCase(); continue; } }
      if (!r.contactPhone) { const m = line.match(phoneRe); if (m) { r.contactPhone = m[1].replace(/\s+/g, ' ').trim(); continue; } }
      if (!r.contactName && !line.includes('@') && line.length > 1 && line.length < 60) { r.contactName = line; continue; }
      if (r.contactName && !r.contactRole && !line.includes('@') && line.length > 1 && line.length < 60) { r.contactRole = line; continue; }
    }
  }
  if (motesIdx >= 0) {
    const noteLines = lines.slice(motesIdx + 1);
    const stop = noteLines.findIndex(l => l.startsWith('Bokad tid:') || l === 'Resurs');
    r.meetingNotes = (stop >= 0 ? noteLines.slice(0, stop) : noteLines).filter(Boolean).join('\n').trim();
  }
  return r;
}
